import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { Box, Button, Stack, Typography } from '@mui/material';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';

export interface SignatureFieldHandle {
    /** La firma como `data:image/png;base64,…`, o `null` si el recuadro está vacío. */
    toPng: () => string | null;
    clear: () => void;
}

interface SignatureFieldProps {
    label: string;
    onChange?: (vacio: boolean) => void;
    disabled?: boolean;
}

/** Ancho máximo del PNG que se manda: el server acepta hasta 300 KB y una firma no necesita más. */
const ANCHO_MAXIMO_PNG = 900;

/**
 * Recuadro de firma (`signature_pad`). Siempre blanco con tinta negra, también en modo oscuro: lo
 * que se firma es lo que se imprime en el PDF del expediente. La densidad se topa en 2x para que el
 * PNG de una tablet de alta resolución no pese de más.
 */
const SignatureField = forwardRef<SignatureFieldHandle, SignatureFieldProps>(({ label, onChange, disabled }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const padRef = useRef<SignaturePad | null>(null);
    const [vacio, setVacio] = useState(true);

    const avisar = useCallback(
        (estaVacio: boolean) => {
            setVacio(estaVacio);
            onChange?.(estaVacio);
        },
        [onChange]
    );

    const ajustarTamano = useCallback(() => {
        const canvas = canvasRef.current;
        const pad = padRef.current;
        if (!canvas || !pad) return;
        const ratio = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
        const { width, height } = canvas.getBoundingClientRect();
        if (!width || !height) return;
        const datos = pad.toData();
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        canvas.getContext('2d')?.scale(ratio, ratio);
        // Cambiar el tamaño borra el lienzo: se vuelve a pintar lo que ya estaba firmado.
        pad.clear();
        if (datos.length) pad.fromData(datos);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const pad = new SignaturePad(canvas, {
            penColor: '#111111',
            backgroundColor: 'rgba(0,0,0,0)',
            minWidth: 0.8,
            maxWidth: 2.6
        });
        padRef.current = pad;
        const alTerminar = () => avisar(pad.isEmpty());
        pad.addEventListener('endStroke', alTerminar);
        ajustarTamano();
        const observador = new ResizeObserver(() => ajustarTamano());
        observador.observe(canvas);
        return () => {
            observador.disconnect();
            pad.removeEventListener('endStroke', alTerminar);
            pad.off();
            padRef.current = null;
        };
    }, [ajustarTamano, avisar]);

    useEffect(() => {
        const pad = padRef.current;
        if (!pad) return;
        if (disabled) pad.off();
        else pad.on();
    }, [disabled]);

    const limpiar = useCallback(() => {
        padRef.current?.clear();
        avisar(true);
    }, [avisar]);

    useImperativeHandle(
        ref,
        () => ({
            clear: limpiar,
            toPng: () => {
                const canvas = canvasRef.current;
                const pad = padRef.current;
                if (!canvas || !pad || pad.isEmpty()) return null;
                const escala = Math.min(1, ANCHO_MAXIMO_PNG / canvas.width);
                if (escala === 1) return canvas.toDataURL('image/png');
                const salida = document.createElement('canvas');
                salida.width = Math.round(canvas.width * escala);
                salida.height = Math.round(canvas.height * escala);
                salida.getContext('2d')?.drawImage(canvas, 0, 0, salida.width, salida.height);
                return salida.toDataURL('image/png');
            }
        }),
        [limpiar]
    );

    return (
        <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary">
                    {label}
                </Typography>
                <Button
                    size="small"
                    color="inherit"
                    startIcon={<RestartAltRoundedIcon fontSize="small" />}
                    onClick={limpiar}
                    disabled={disabled || vacio}
                    sx={{ py: 0.25 }}
                >
                    Borrar
                </Button>
            </Stack>
            <Box
                sx={{
                    position: 'relative',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: '#FFFFFF',
                    overflow: 'hidden',
                    opacity: disabled ? 0.6 : 1
                }}
            >
                <Box
                    component="canvas"
                    ref={canvasRef}
                    sx={{ display: 'block', width: '100%', height: { xs: 180, sm: 220 }, touchAction: 'none' }}
                />
                {/* Renglón guía, como en el papel. */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 24,
                        right: 24,
                        bottom: 40,
                        borderBottom: '1px dashed #C4C4C4',
                        pointerEvents: 'none'
                    }}
                />
                {vacio && (
                    <Typography
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 14,
                            textAlign: 'center',
                            fontSize: '0.8rem',
                            color: '#8A8A8A',
                            pointerEvents: 'none'
                        }}
                    >
                        Firma aquí con el dedo
                    </Typography>
                )}
            </Box>
        </Stack>
    );
});

SignatureField.displayName = 'SignatureField';

export default SignatureField;
